import {
  AccessTime,
  CancelOutlined,
  InfoOutlined,
  LocalShipping,
  Warning,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SahinSwitch } from 'common/components/SahinSwitch/SahinSwitch';
import { AdminTablePaths } from 'common/model/AdminTablePaths';
import { postAPI, useFetch } from '@sahinyazilim/sahinyazilim-components';

const defaultAutoStatusSettings = {
  isEnabled: false,
  pendingToRejectedHours: 2,
  onWayToDeliveredHours: 2,
};

const statusSteps = [
  {
    from: 'PENDING',
    to: 'REJECTED',
    icon: <CancelOutlined sx={{ fontSize: 24, color: 'white' }} />,
    time: '2 saat',
    description:
      'Bekleyen siparişlere 2 saat herhangi bir işlem yapılmazsa otomatik olarak iptal edilir',
  },
  {
    from: 'ON_WAY',
    to: 'DELIVERED',
    icon: <LocalShipping sx={{ fontSize: 24, color: 'white' }} />,
    time: '2 saat',
    description:
      'Yola çıkan siparişler 2 saat güncelleme almazsa otomatik olarak teslim edildi olarak işaretlenir',
  },
];

export const AutoStatusToolbar = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: autoStatusData, mutate: mutateAutoStatus } = useFetch<{
    isEnabled: boolean;
    pendingToRejectedHours: number;
    onWayToDeliveredHours: number;
  }>(AdminTablePaths.AUTO_ORDER_STATUS);

  const autoStatusSettings = autoStatusData || defaultAutoStatusSettings;
  const isEnabled = autoStatusSettings.isEnabled;

  const handleInfoClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAutoStatusToggle = useCallback(
    async (enabled: boolean) => {
      try {
        const updatedSettings = {
          ...defaultAutoStatusSettings,
          isEnabled: enabled,
        };
        const response = await postAPI(
          AdminTablePaths.AUTO_ORDER_STATUS,
          updatedSettings,
        );
        if (response.status === 'success') {
          mutateAutoStatus();
        }
      } catch (error) {
        console.error(
          'Otomatik sipariş durumu ayarı güncellenirken hata:',
          error,
        );
      }
    },
    [mutateAutoStatus],
  );

  return (
    <>
      <Grid container>
        <Grid
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
            p: [0.5, 0],
            width: '90px',
          }}
          wrap="nowrap"
          justifyContent="space-between"
          alignItems="center"
          container
        >
          <Grid item>
            <Tooltip
              title="Sipariş durumları otomatik ilerlesin"
              placement="top"
            >
              <SahinSwitch
                value={isEnabled}
                label=""
                size={0.8}
                onChange={handleAutoStatusToggle}
              />
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Otomatik ilerleme hakkında bilgi" placement="top">
              <IconButton onClick={handleInfoClick} size="small">
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>

      {/* Auto Order Status Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              🚀 Otomatik Sipariş Durumu İlerlemesi
            </Typography>
            <Chip
              label="BETA"
              size="small"
              color="warning"
              icon={<Warning sx={{ fontSize: 16 }} />}
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#ff9800',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Bu özellik açıkken belirli durumlardaki siparişleriniz otomatik
            olarak güncellenir
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
              ⚙️ Nasıl Çalışır?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Otomatik güncelleme özelliği aktif olduğunda, belirli durumlardaki
              siparişleriniz belirlenen süreler sonunda otomatik olarak
              güncellenir. Bu sayede unutulan siparişlerin durumu otomatik
              olarak güncel kalır.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
              📋 Otomatik Güncelleme Kuralları
            </Typography>
            <Grid container spacing={2}>
              {statusSteps.map((step, index) => (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      backgroundColor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background:
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              borderRadius: '12px',
                              background:
                                'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                            },
                          }}
                        >
                          <Box sx={{ position: 'relative', zIndex: 1 }}>
                            {step.icon}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={t(`orderStatus.${step.from.toLowerCase()}`)}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            →
                          </Typography>
                          <Chip
                            label={t(`orderStatus.${step.to.toLowerCase()}`)}
                            size="small"
                            color="primary"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            border: '1px solid rgba(25, 118, 210, 0.2)',
                          }}
                        >
                          <AccessTime sx={{ fontSize: 18, color: '#1976d2' }} />
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color="#1976d2"
                          >
                            {step.time}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{
              p: 2.5,
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2,
              mb: 2,
              border: '1px solid rgba(25, 118, 210, 0.12)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>💡 Not:</strong> Bu otomatik güncellemeler sadece
              belirtilen durumlar için geçerlidir. Diğer durumlar manuel olarak
              güncellenmelidir. Ayrıca, istediğiniz zaman manuel olarak sipariş
              durumunu değiştirebilirsiniz.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              backgroundColor: 'rgba(255, 152, 0, 0.04)',
              borderRadius: 2,
              border: '1px solid rgba(255, 152, 0, 0.12)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>📝 Geliştirme Aşaması:</strong> Bu özellikler şu anda
              geliştirme aşamasındadır. Sizin düşünceleriniz bizim için çok
              önemli! Nasıl bir işleyiş olması gerektiği konusundaki
              önerilerinizi bizimle iletişim kanalları üzerinden
              paylaşabilirsiniz. Geri bildirimleriniz bu özelliği daha da
              iyileştirmemize yardımcı olacaktır.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            color="primary"
          >
            Anladım
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
